import React from 'react'
import { ResumeItem } from '../../services/apiService'
import { ResumeFitAnalysisResponse } from '../../services/jobApplicationService'
import './Step1FitAnalysis.css'

interface Step1FitAnalysisProps {
  fitAnalysis: ResumeFitAnalysisResponse | null
  availableResumes: ResumeItem[]
  onNext: () => void
}

export const Step1FitAnalysis: React.FC<Step1FitAnalysisProps> = ({
  fitAnalysis,
  availableResumes,
  onNext,
}) => {
  const getFitScoreColor = (score: number): string => {
    if (score >= 80) return 'excellent'
    if (score >= 60) return 'good'
    if (score >= 40) return 'moderate'
    return 'low'
  }

  const getFitScoreLabel = (score: number): string => {
    if (score >= 80) return 'Excellent Match'
    if (score >= 60) return 'Good Match'
    if (score >= 40) return 'Moderate Match'
    return 'Low Match'
  }

  // Sort resumes by fit score (highest first)
  const sortedAnalyses = fitAnalysis?.resumeFitAnalyses
    ? [...fitAnalysis.resumeFitAnalyses].sort((a, b) => b.fitScore - a.fitScore)
    : []

  return (
    <div className="step1-fit-analysis">
      <div className="step-header">
        <h3>Resume Fit Analysis</h3>
        <p className="step-description">
          Review how well each of your resumes matches this job application. 
          The fit score is calculated based on skills, experience, and qualifications.
        </p>
      </div>

      {sortedAnalyses.length === 0 ? (
        <div className="step1-no-analysis">
          <div className="step1-no-analysis-icon">📊</div>
          <h4>No Fit Analysis Available</h4>
          <p>
            {fitAnalysis?.noResumesMessage || 
             'Fit analysis has not been performed yet. You can still select a resume in the next step.'}
          </p>
        </div>
      ) : (
        <div className="step1-resume-cards">
          {sortedAnalyses.map((analysis) => {
            const resume = availableResumes.find(r => r.id === analysis.resumeId)
            if (!resume) return null

            return (
              <div key={analysis.resumeId} className="step1-resume-card">
                <div className="step1-resume-card-header">
                  <div className="step1-resume-card-title">
                    <h4>{resume.originalFilename || resume.filename || 'Resume'}</h4>
                    {analysis.fitScore >= 80 && (
                      <span className="step1-best-fit-badge">Best Fit</span>
                    )}
                  </div>
                  <div className={`step1-fit-score-badge ${getFitScoreColor(analysis.fitScore)}`}>
                    <span className="step1-fit-score-value">{Math.round(analysis.fitScore)}%</span>
                    <span className="step1-fit-score-label">{getFitScoreLabel(analysis.fitScore)}</span>
                  </div>
                </div>
                <div className="step1-resume-card-body">
                  <div className="step1-resume-card-details">
                    <div className="step1-detail-item">
                      <span className="step1-detail-label">Skills Match:</span>
                      <span className="step1-detail-value">{Math.round(analysis.skillsMatch)}%</span>
                    </div>
                    <div className="step1-detail-item">
                      <span className="step1-detail-label">Experience Match:</span>
                      <span className="step1-detail-value">{Math.round(analysis.experienceMatch)}%</span>
                    </div>
                    <div className="step1-detail-item">
                      <span className="step1-detail-label">Education Match:</span>
                      <span className="step1-detail-value">{Math.round(analysis.educationMatch)}%</span>
                    </div>
                  </div>
                  {analysis.strengths && analysis.strengths.length > 0 && (
                    <div className="step1-resume-card-strengths">
                      <strong>Strengths:</strong>
                      <ul>
                        {analysis.strengths.slice(0, 3).map((strength, idx) => (
                          <li key={idx}>{strength}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className="step-actions">
        <button className="btn btn-primary" onClick={onNext}>
          Continue to Selection →
        </button>
      </div>
    </div>
  )
}

